/**
 * FullDiagnosisReport - Complete Diagnosis Results Component
 * 
 * Why this matters: Displays the complete diagnosis with all issues,
 * recommendations, and care advice after email capture.
 * Now includes uploaded images in hero section for presentation-style report.
 */

import React, { useState } from 'react';
import { 
  CheckCircle, 
  AlertTriangle, 
  AlertCircle, 
  RefreshCw,
  Calendar,
  Sparkles,
  ImageIcon,
  X,
  ZoomIn
} from 'lucide-react';
import { DiagnosisResult } from './CitrusDoctor';
import { UploadedImage } from './ImageUpload';
import IssueCard from './IssueCard';
import RecommendationCard from './RecommendationCard';

interface FullDiagnosisReportProps {
  diagnosis: DiagnosisResult;
  images?: UploadedImage[];
  email?: string;
  onStartOver: () => void;
}

const FullDiagnosisReport: React.FC<FullDiagnosisReportProps> = ({
  diagnosis,
  images = [],
  email,
  onStartOver
}) => {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  /**
   * Get health status display info
   */
  const getHealthStatus = () => {
    switch (diagnosis.overallHealth) {
      case 'healthy':
        return {
          label: 'Healthy',
          color: 'status-healthy',
          icon: CheckCircle,
          message: 'Your citrus tree is in great condition!'
        };
      case 'minor_issues':
        return {
          label: 'Minor Issues Detected',
          color: 'status-minor',
          icon: AlertCircle,
          message: 'A few things need your attention, but overall your tree is doing well.'
        };
      case 'moderate_issues':
        return {
          label: 'Moderate Issues Found',
          color: 'status-moderate',
          icon: AlertTriangle,
          message: 'Several issues have been identified. Follow our recommendations to restore your tree\'s health.'
        };
      case 'severe_issues':
        return {
          label: 'Urgent Attention Needed',
          color: 'status-severe',
          icon: AlertTriangle,
          message: 'Your tree needs immediate care. Please follow the priority recommendations below.'
        };
      default:
        return {
          label: 'Analysis Complete',
          color: 'status-moderate',
          icon: CheckCircle,
          message: 'See your detailed diagnosis below.'
        };
    }
  };

  const healthStatus = getHealthStatus();
  const StatusIcon = healthStatus.icon;

  /**
   * Format date for display
   */
  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  /**
   * Sort recommendations by priority
   */
  const sortedRecommendations = [...diagnosis.recommendations].sort((a, b) => a.priority - b.priority);

  return (
    <div className="doctor-step doctor-step-results">
      {/* Success Message */}
      {email && (
        <div className="results-email-confirmation">
          <CheckCircle size={20} />
          <span>Full report sent to <strong>{email}</strong></span>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="image-lightbox"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="lightbox-close"
            onClick={() => setLightboxImage(null)}
          >
            <X size={24} />
          </button>
          <img 
            src={lightboxImage} 
            alt="Zoomed view" 
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Hero Section with Images and Health Score */}
      <div className={`results-hero ${healthStatus.color}`}>
        {/* Uploaded Images - Clickable */}
        {images.length > 0 ? (
          <div className={`results-hero-images ${images.length === 1 ? 'single-image' : ''}`}>
            {images.slice(0, 4).map((img, index) => (
              <div 
                key={img.id} 
                className="results-hero-image clickable"
                onClick={() => setLightboxImage(img.previewUrl)}
              >
                <img src={img.previewUrl} alt={img.label || `Tree image ${index + 1}`} />
                <div className="image-zoom-hint">
                  <ZoomIn size={20} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="results-hero-images single-image">
            <div className="results-hero-image" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.1)'
            }}>
              <ImageIcon size={48} style={{ opacity: 0.5 }} />
            </div>
          </div>
        )}

        {/* Health Score & Status */}
        <div className="results-hero-content">
          <div className="results-hero-score">
            <div className="score-numbers">
              <span className="score-value">{diagnosis.healthScore}</span>
              <span className="score-max">/100</span>
            </div>
            <span className="score-label">Health Score</span>
          </div>
          
          <div className={`results-hero-status`}>
            <StatusIcon size={20} />
            <span>{healthStatus.label}</span>
          </div>
          
          <p className="results-hero-message">{healthStatus.message}</p>
          
          <div className="results-hero-meta">
            <span>
              <Calendar size={16} />
              {formatDate(diagnosis.analysisTimestamp)}
            </span>
          </div>
        </div>
      </div>

      {/* Report Body */}
      <div className="results-body">

      {/* Issues Section */}
      {diagnosis.issues.length > 0 && (
        <div className="results-section">
          <h2 className="results-section-title">
            <AlertCircle size={20} />
            Issues Identified ({diagnosis.issues.length})
          </h2>
          <div className="issues-list">
            {diagnosis.issues.map((issue, index) => (
              <IssueCard 
                key={issue.id} 
                issue={issue} 
                index={index} 
                imageUrl={images[0]?.previewUrl}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recommendations Section */}
      {sortedRecommendations.length > 0 && (
        <div className="results-section">
          <h2 className="results-section-title">
            <Sparkles size={20} />
            Recommended Actions ({sortedRecommendations.length})
          </h2>
          <div className="recommendations-list">
            {sortedRecommendations.map((rec, index) => (
              <RecommendationCard key={index} recommendation={rec} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* General Care Advice */}
      {diagnosis.generalCareAdvice && (
        <div className="results-section results-care-advice">
          <h2 className="results-section-title">
            <CheckCircle size={20} />
            Expert Care Advice
          </h2>
          <div className="care-advice-card">
            <p>{diagnosis.generalCareAdvice}</p>
          </div>
        </div>
      )}

      {/* Seasonal Considerations */}
      {diagnosis.seasonalConsiderations && (
        <div className="results-section results-seasonal">
          <h2 className="results-section-title">
            <Calendar size={20} />
            Seasonal Considerations
          </h2>
          <div className="seasonal-card">
            <p>{diagnosis.seasonalConsiderations}</p>
          </div>
        </div>
      )}

      {/* Three Plant Pillars CTA */}
      <div className="results-pillars-cta">
        <div className="pillars-cta-content">
          <h3>The Three Plant Pillars</h3>
          <p>
            For the best results, implement all three pillars: mineral-based soil, 
            live microbes, and organic fertilizer. This creates a complete ecosystem 
            that supports healthy citrus growth.
          </p>
          <div className="pillars-products">
            <a 
              href="https://uscitrusnursery.com/collections/dr-manis-magic" 
              target="_blank" 
              rel="noopener noreferrer"
              className="pillars-shop-btn"
            >
              Shop Dr. Mani's Magic Products →
            </a>
          </div>
        </div>
      </div>

      {/* Start Over */}
      <div className="results-footer">
        <button 
          className="doctor-btn doctor-btn-secondary"
          onClick={onStartOver}
        >
          <RefreshCw size={18} />
          Diagnose Another Tree
        </button>
      </div>
      </div> {/* End results-body */}
    </div>
  );
};

export default FullDiagnosisReport;
